/**
 * SingleSelectDropdown Component
 *
 * A reusable dropdown component for selecting a single item.
 */

import { useState, useRef, useEffect } from 'react';
import { ArrowIcon } from '../../../icons';
import styles from '../MultiSelectDropdown/SelectDropdown.module.scss';

export interface SelectDropdownOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SelectDropdownProps {
  /** Placeholder text shown when nothing is selected */
  placeholder?: string;
  /** Available options */
  options: SelectDropdownOption[];
  /** Currently selected value */
  value?: string;
  /** Callback when selection changes */
  onChange: (value: string) => void;
  /** Dropdown header title */
  title?: string;
  /** Custom CSS class */
  className?: string;
  /** Direction the menu opens relative to the trigger (default: 'down') */
  direction?: 'up' | 'down' | 'left' | 'right';
  children?: React.ReactNode;
}

export const SelectDropdown = ({
  placeholder = 'Select...',
  options,
  value,
  onChange,
  title,
  className = '',
  direction = 'down',
  children,
}: SelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(300);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !event.composedPath().includes(dropdownRef.current)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll so menu doesn't detach from trigger
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && e.composedPath().includes(dropdownRef.current)) return;
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setSelectedValue(optionValue);
  };

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      switch (direction) {
        case 'up': {
          const available = rect.top - 8;
          setMenuStyle({ bottom: window.innerHeight - rect.top + 4, left: rect.left, minWidth: rect.width });
          setMenuMaxHeight(Math.max(120, Math.min(300, available)));
          break;
        }
        case 'left': {
          const available = rect.bottom - 8;
          setMenuStyle({ bottom: window.innerHeight - rect.bottom, right: window.innerWidth - rect.left + 4 });
          setMenuMaxHeight(Math.max(120, Math.min(300, available)));
          break;
        }
        case 'right': {
          const available = rect.bottom - 8;
          setMenuStyle({ bottom: window.innerHeight - rect.bottom, left: rect.right + 4 });
          setMenuMaxHeight(Math.max(120, Math.min(300, available)));
          break;
        }
        default: {
          const available = window.innerHeight - rect.bottom - 8;
          setMenuStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
          setMenuMaxHeight(Math.max(120, Math.min(300, available)));
          break;
        }
      }
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div className={`${styles.selectDropdown} ${className} ${className === 'small' ? styles.small : ''}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={handleToggle}
      >
        <span className={styles.label}>{options.find(o => o.value === selectedValue)?.label ?? placeholder}</span>
        <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}>
          <ArrowIcon />
        </span>
      </button>

      {isOpen && (
        <div className={`${styles.menu}`} style={{ ...menuStyle, maxHeight: menuMaxHeight }}>
          {title && (
            <div className={styles.header}>
              <p className={styles.title}>{title}</p>
            </div>
          )}

          <ul className={styles.options}>
            {options.map((option) => (
              <li className={`${styles.optionRow} ${option.value === selectedValue ? styles.optionRowSelected : ''}`}>
                <div
                  key={option.value}
                  className={`${styles.option}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                  <span className={styles.optionLabel}>{option.label}</span>
                </div>
              </li>
            ))}
          </ul>

          {children}
        </div>
      )}
    </div>
  );
};
